import type {AppError, Result} from '@packages/utils';
import {useQueryClient} from '@tanstack/react-query';
import type {UseQueryResult} from '@tanstack/react-query';
import {useRouter} from 'next/router';
import {useCallback, useMemo, useRef, useState} from 'react';
import {ENTRY_MANAGEMENTS_PROGRESS} from '../../../config/constants/routes';
import {
  type StepCandidateType,
  useDeleteEntryManagement,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import type {GetDepartmentsInSummaryResponse} from '../../../core/repositories/reportSummaryRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
  showWarningSnackBar,
} from '../../../shared/components';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import type {ProgressStepDrawerRef} from '../components/Container/Progress/ProgressStepDrawerContainer';
import type {StepCandidatePreviewModalRef} from '../components/Container/Progress/StepCandidatePreviewModalContainer';
import type {StepCreationTriggerType} from '../constants/entrySourceTypes';
import {
  INPUT_METHOD_MANUAL,
  INPUT_METHOD_REPORT,
  INPUT_METHOD_SCREENSHOT,
  INPUT_METHOD_TEXT,
} from '../constants/inputMethods';
import {QUERY_MODE_ADD_MANAGEMENT} from '../constants/queryParams';
import {useAiTodoGeneration} from './useAiTodoGeneration';
import {CACHE_KEYS} from './useEntryManagementUpdate';
import {useProgressDeleteModal} from './useProgressDeleteModal';
import {useProgressModalStates} from './useProgressModalStates';
import {useStepCandidateSaveActions} from './useStepCandidateSaveActions';

/**
 * エントリー管理画面のオーバーレイ（Modal/Drawer）制御ロジックを共通化したカスタムフック
 *
 * @description
 * - モーダル/ドロワーの開閉状態管理（useProgressModalStates）
 * - ステップ削除モーダルの管理（useProgressDeleteModal）
 * - エントリー管理削除の処理
 * - 入力方法選択後のモーダル遷移制御
 * - ステップ候補の保存処理
 * - AI TODO生成機能との連携
 * - 体験記からAI自動入力の表示条件計算
 *
 * @param companyID - （オプショナル）会社ID（体験記表示判定に使用）。未指定の場合、hasReportDataは常にfalse
 * @param departmentsData - （オプショナル）部門データ（体験記表示判定に使用）。未指定の場合、hasReportDataは常にfalse
 * @returns オーバーレイ制御に必要な関数とref、状態変数、hasReportData
 */
export const useProgressOverlays = (
  companyID?: number | null,
  departmentsData?: UseQueryResult<
    Result<GetDepartmentsInSummaryResponse, AppError>
  >,
) => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const queryClient = useQueryClient();

  // モーダル状態の統合管理
  const modalStates = useProgressModalStates();
  const {isAddingEntryManagement, setIsAddingEntryManagement} = modalStates;

  /**
   * クエリパラメータにmode=add-managementを追加
   * AppのWebView上ではURL操作を行わない
   */
  const addManagementModeToQuery = useCallback(() => {
    if (isWebView) return;

    const currentQuery = {...router.query};
    currentQuery.mode = QUERY_MODE_ADD_MANAGEMENT;
    void router.push(
      {
        pathname: router.pathname,
        query: currentQuery,
      },
      undefined,
      {shallow: true},
    );
  }, [router]);

  /**
   * クエリパラメータからmode=add-managementを削除
   * AppのWebView上ではURL操作を行わない
   */
  const removeManagementModeFromQuery = useCallback(() => {
    if (isWebView) return;

    const currentQuery = {...router.query};
    if (currentQuery.mode === QUERY_MODE_ADD_MANAGEMENT) {
      const {mode, ...rest} = currentQuery;
      void router.push(
        {
          pathname: router.pathname,
          query: rest,
        },
        undefined,
        {shallow: true},
      );
    }
  }, [router]);

  /**
   * InputMethodModalの閉じる処理をラップして、クエリパラメータを削除
   */
  const closeInputMethodModal = useCallback(() => {
    modalStates.modals.inputMethodModal.close();
    removeManagementModeFromQuery();
  }, [modalStates.modals.inputMethodModal, removeManagementModeFromQuery]);

  // modalsオブジェクトをカスタムcloseハンドラーで上書き
  // useMemoで安定化し、不要な再レンダリングを防止
  const modals = useMemo(
    () => ({
      ...modalStates.modals,
      inputMethodModal: {
        ...modalStates.modals.inputMethodModal,
        close: closeInputMethodModal,
      },
    }),
    [modalStates.modals, closeInputMethodModal],
  );

  // 削除モーダルの管理（step削除用）
  const {deleteModal, handleOpenDeleteModal, handleConfirmDelete} =
    useProgressDeleteModal();

  // エントリー管理削除の状態管理
  const [entryManagementIdToDelete, setEntryManagementIdToDelete] = useState<
    number | null
  >(null);

  // エントリー管理削除のAPI
  const {mutateAsync: deleteEntryManagement} = useDeleteEntryManagement();

  // 候補保存アクション
  const {saveCandidate} = useStepCandidateSaveActions();

  // AI TODO生成機能
  const aiTodoGeneration = useAiTodoGeneration();

  // ドロワー制御用のref
  const stepDrawerRef = useRef<ProgressStepDrawerRef>(null);
  const stepCandidatePreviewModalRef =
    useRef<StepCandidatePreviewModalRef>(null);

  // 体験記からAI自動入力の表示条件
  const hasReportData = useMemo(() => {
    // エントリー管理追加モード時は体験記オプションを表示しない
    if (isAddingEntryManagement) return false;
    if (!companyID || companyID === 0) return false;
    if (!departmentsData?.data?.ok) return false;

    const departments = departmentsData.data.value.departments;
    return (departments?.length ?? 0) > 0;
  }, [isAddingEntryManagement, companyID, departmentsData?.data]);

  /**
   * 入力方法選択後のモーダル遷移を処理
   */
  const handleMethodSelected = useCallback(
    (method: string) => {
      modals.inputMethodModal.close();

      if (isAddingEntryManagement) {
        // エントリー管理追加モード
        switch (method) {
          case INPUT_METHOD_MANUAL:
            modals.addMgtModal.open();
            break;
          case INPUT_METHOD_SCREENSHOT:
            modals.screenshotModal.open();
            break;
          case INPUT_METHOD_TEXT:
            modals.textImportModal.open();
            break;
        }
      } else {
        // ステップ追加モード
        switch (method) {
          case INPUT_METHOD_REPORT:
            modals.reportAnalyzeModal.open();
            break;
          case INPUT_METHOD_SCREENSHOT:
            modals.screenshotModal.open();
            break;
          case INPUT_METHOD_TEXT:
            modals.textImportModal.open();
            break;
          case INPUT_METHOD_MANUAL:
            stepDrawerRef.current?.openAdd();
            break;
        }
      }
    },
    [modals, isAddingEntryManagement],
  );

  /**
   * 候補から保存する
   */
  const handleSaveCandidate = useCallback(
    async (
      companyName: string | null,
      companyID: number | null,
      userEntryManagementID: number | undefined,
      candidate: StepCandidateType,
      entrySourceType: StepCreationTriggerType,
      showNotification = true,
    ) => {
      const result = await saveCandidate(
        companyName,
        companyID,
        userEntryManagementID,
        candidate,
        entrySourceType,
      );

      if (!result.success) {
        return undefined;
      }

      if (showNotification) {
        if (result.googleSyncFailed) {
          showWarningSnackBar({
            title: 'Google同期失敗',
            message:
              '選考予定は保存されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます',
          });
        } else {
          showSuccessSnackBar({
            title: 'ステップを保存しました',
            message: '',
          });
        }
      }

      return {
        stepID: result.stepID,
        userEntryManagementID: result.userEntryManagementID,
      };
    },
    [saveCandidate],
  );

  /**
   * エントリー管理追加ボタンのクリックハンドラー
   */
  const handleAddEntryManagement = useCallback(() => {
    setIsAddingEntryManagement(true);
    addManagementModeToQuery();
    modals.inputMethodModal.open();
  }, [
    modals.inputMethodModal,
    setIsAddingEntryManagement,
    addManagementModeToQuery,
  ]);

  /**
   * 選考ステップ追加ボタンのクリックハンドラー
   */
  const handleAddStep = useCallback(() => {
    setIsAddingEntryManagement(false);
    modals.inputMethodModal.open();
  }, [modals.inputMethodModal, setIsAddingEntryManagement]);

  /**
   * エントリー管理編集ボタンのクリックハンドラー
   */
  const handleEditEntry = useCallback(() => {
    modals.editEntryModal.open();
  }, [modals.editEntryModal]);

  /**
   * エントリー管理削除ボタンのクリックハンドラー
   */
  const handleDeleteEntry = useCallback(
    (entryManagementId: number) => {
      setEntryManagementIdToDelete(entryManagementId);
      modals.deleteEntryModal.open();
    },
    [modals.deleteEntryModal],
  );

  /**
   * overallStatus更新モーダルを開く
   */
  const handleOpenOverallStatusUpdateModal = useCallback(() => {
    modals.overallStatusUpdateModal.open();
  }, [modals.overallStatusUpdateModal]);

  /**
   * 体験記からステップ生成成功時の処理
   */
  const handleReportAnalyzeSuccess = useCallback(
    (result: {
      candidates: StepCandidateType[];
      companyID?: number | null;
      companyName?: string | null;
      userEntryManagementID?: number;
    }) => {
      // 候補プレビューモーダルを開く（既存機能を再利用）
      stepCandidatePreviewModalRef.current?.open(result, 'report');
    },
    [],
  );

  /**
   * エントリー管理削除を確定して実行する
   */
  const handleConfirmDeleteEntry = useCallback(async () => {
    if (!entryManagementIdToDelete) {
      showErrorSnackBar({
        title: '削除エラー',
        message: '削除対象のエントリー管理が見つかりません',
      });
      return;
    }

    try {
      const result = await deleteEntryManagement({
        id: entryManagementIdToDelete,
      });

      if (result.ok) {
        showSuccessSnackBar({
          title: '削除完了',
          message: 'エントリー管理を削除しました',
        });
        modals.deleteEntryModal.close();
        setEntryManagementIdToDelete(null);

        // キャッシュを無効化して削除を即座に反映
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.PAGINATED_ENTRY_MANAGEMENTS],
        });
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.ENTRY_MANAGEMENTS],
        });

        // 削除成功後、進捗一覧ページにリダイレクト
        if (isWebView) {
          location.href = ENTRY_MANAGEMENTS_PROGRESS;
        } else {
          void router.push(ENTRY_MANAGEMENTS_PROGRESS);
        }
      } else {
        showErrorSnackBar({
          title: '削除失敗',
          message: 'エントリー管理の削除に失敗しました。もう一度お試しください',
        });
      }
    } catch (_error) {
      showErrorSnackBar({
        title: '削除失敗',
        message: 'エントリー管理の削除に失敗しました。もう一度お試しください',
      });
    }
  }, [
    entryManagementIdToDelete,
    deleteEntryManagement,
    modals.deleteEntryModal,
    queryClient,
    router,
  ]);

  return {
    modals,
    deleteModal,
    isAddingEntryManagement,
    setIsAddingEntryManagement,
    stepDrawerRef,
    stepCandidatePreviewModalRef,
    handleMethodSelected,
    handleSaveCandidate,
    handleAddEntryManagement,
    handleAddStep,
    handleEditEntry,
    handleDeleteEntry,
    handleOpenDeleteModal,
    handleConfirmDelete,
    handleConfirmDeleteEntry,
    handleOpenOverallStatusUpdateModal,
    handleReportAnalyzeSuccess,
    aiTodoGeneration,
    hasReportData,
  };
};
